"use client"

import dayjs from "dayjs"
import type { UtilizationItem } from "types/employee-utilization"

interface UtilizationEntry {
  id: string
  employee_name: string
  date: string
  category: string
  hours: number
  status: "Pending" | "Manager Approved" | "Manager Declined"
  created_at: string
  items: UtilizationItem[]
}

interface ApprovalViewProps {
  utilizationEntries: UtilizationEntry[]
  startDate: string
  endDate: string
  statusFilter: "all" | "pending" | "approved" | "declined"
  selectedEntries: Set<string>
  isProcessing: boolean
  onSelectEntry: (entryId: string) => void
  onSelectAll: () => void
  onApproveEntry: (entryId: string) => void
  onDeclineEntry: (entryId: string) => void
  onBulkApprove: () => void
  onBulkDecline: () => void
  isLoading: boolean
}

export default function ApprovalView({
  utilizationEntries,
  startDate,
  endDate,
  statusFilter,
  selectedEntries,
  isProcessing,
  onSelectEntry,
  onSelectAll,
  onApproveEntry,
  onDeclineEntry,
  onBulkApprove,
  onBulkDecline,
  isLoading,
}: ApprovalViewProps) {
  const getFilteredEntries = () => {
    return utilizationEntries.filter((entry) => {
      const entryDate = dayjs(entry.date).format("YYYY-MM-DD")
      const matchesDateRange = (!startDate || entryDate >= startDate) && (!endDate || entryDate <= endDate)

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "pending" && entry.status === "Pending") ||
        (statusFilter === "approved" && entry.status === "Manager Approved") ||
        (statusFilter === "declined" && entry.status === "Manager Declined")

      return matchesDateRange && matchesStatus
    })
  }

  const getHoursColorClass = (hours: number) => {
    if (hours < 6) {
      return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
    } else if (hours >= 6 && hours <= 7) {
      return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
    } else if (hours === 8) {
      return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
    } else {
      return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
      case "Manager Approved":
        return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
      case "Manager Declined":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400"
    }
  }

  const filteredEntries = getFilteredEntries()

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-100 dark:border-navy-700 overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-100 dark:border-navy-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-navy-700 bg-gray-50 dark:bg-navy-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-50 dark:bg-brand-900/20 rounded-lg flex items-center justify-center">
              <i className="fas fa-check-circle text-brand-600 dark:text-brand-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-navy-700 dark:text-white">Utilization Approvals</h3>
          </div>

          {selectedEntries.size > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">{selectedEntries.size} selected</span>
              <button
                type="button"
                onClick={onBulkApprove}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg font-medium transition-all text-sm disabled:opacity-50"
              >
                <i className="fas fa-check mr-2"></i>
                Approve Selected
              </button>
              <button
                type="button"
                onClick={onBulkDecline}
                disabled={isProcessing}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-all text-sm disabled:opacity-50"
              >
                <i className="fas fa-times mr-2"></i>
                Decline Selected
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="max-h-[500px] overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-navy-700/30">
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedEntries.size === filteredEntries.length && filteredEntries.length > 0}
                    onChange={onSelectAll}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                </th>
                <th className="px-4 py-3 text-center font-semibold text-navy-700 dark:text-white">Employee</th>
                <th className="px-4 py-3 text-center font-semibold text-navy-700 dark:text-white">Date</th>
                <th className="px-4 py-3 text-center font-semibold text-navy-700 dark:text-white">Category</th>
                <th className="px-4 py-3 text-center font-semibold text-navy-700 dark:text-white">Hours</th>
                <th className="px-4 py-3 text-center font-semibold text-navy-700 dark:text-white">Status</th>
                <th className="px-4 py-3 text-center font-semibold text-navy-700 dark:text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-navy-700 rounded-full flex items-center justify-center">
                        <i className="fas fa-inbox text-2xl text-gray-400"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-navy-700 dark:text-white mb-1">No Records Found</h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          No utilization records found for the selected filters.
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredEntries.map((entry, index) => (
                  <tr
                    key={entry.id}
                    className={`border-b border-gray-100 dark:border-navy-700 hover:bg-gray-50 dark:hover:bg-navy-700/30 transition-colors ${
                      index % 2 === 0 ? "bg-white dark:bg-navy-800" : "bg-gray-50/50 dark:bg-navy-700/20"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedEntries.has(entry.id)}
                        onChange={() => onSelectEntry(entry.id)}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-navy-700 dark:text-white text-center">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-white font-medium text-xs">
                          {entry.employee_name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </div>
                        <span className="text-sm">{entry.employee_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-navy-700 dark:text-white text-center">
                      {dayjs(entry.date).format("MMM DD, YYYY")}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400">
                        {entry.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center justify-center w-12 h-6 rounded-md font-medium text-xs ${getHoursColorClass(entry.hours)}`}
                      >
                        {entry.hours}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(entry.status)}`}
                      >
                        {entry.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {entry.status === "Pending" ? (
                        <div className="flex items-center gap-2 justify-center">
                          <button
                            type="button"
                            onClick={() => onApproveEntry(entry.id)}
                            disabled={isProcessing}
                            className="px-3 py-1 bg-green-700 hover:bg-green-600 text-white rounded text-xs font-medium transition-all disabled:opacity-50"
                          >
                            <i className="fas fa-check mr-1"></i>
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeclineEntry(entry.id)}
                            disabled={isProcessing}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium transition-all disabled:opacity-50"
                          >
                            <i className="fas fa-times mr-1"></i>
                            Decline
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">No actions</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
